import { Link } from 'react-router-dom';
import { MapPin, Users, Target, HandHeart, Signpost, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { Project } from '@/types';
import { formatNumber } from '@/lib/utils';
import { getProjectAvenueName } from '@/lib/project-avenue';

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-500',
  PENDING_APPROVAL: 'bg-yellow-500',
  PUBLISHED: 'bg-green-500',
  ARCHIVED: 'bg-red-500',
};

function formatIndianRupee(amount: number): string {
  return `₹${amount.toLocaleString('en-IN')}`;
}

function stripHtml(html: string | null | undefined) {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

interface ProjectCardProps {
  project: Project;
  communityId: string;
}

export function ProjectCard({ project, communityId }: ProjectCardProps) {
  const isFromOtherCommunity = project.communityId !== communityId;
  const avenueName = getProjectAvenueName(project);

  return (
    <Link to={`/${communityId}/projects/${project.id}`}>
      <Card className="cursor-pointer hover:shadow-md transition-all h-full">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-lg line-clamp-2">{project.name}</CardTitle>
            <Badge
              className={`${statusColors[project.status] || 'bg-gray-500'} text-white shrink-0`}
            >
              {project.status.replace('_', ' ')}
            </Badge>
          </div>
          {isFromOtherCommunity && project.community && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              From: {project.community.name}
            </p>
          )}
          {project.isDistrictProject && (
            <Badge
              variant="outline"
              className="w-fit gap-1 font-normal border-primary/30 text-primary"
            >
              District Project
            </Badge>
          )}
          {avenueName && (
            <Badge variant="secondary" className="w-fit gap-1 font-normal">
              <Signpost className="h-3 w-3" />
              {avenueName}
            </Badge>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {project.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {stripHtml(project.description)}
            </p>
          )}
          <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
            {project.location && (
              <div className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                <span className="line-clamp-1">{project.location}</span>
              </div>
            )}
            {project.estimatedBeneficiaries && (
              <div className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                <span>{formatNumber(project.estimatedBeneficiaries)} beneficiaries</span>
              </div>
            )}
            {project.expectedCost && (
              <div className="flex items-center gap-1">
                <HandHeart className="h-3.5 w-3.5" />
                <span>{formatIndianRupee(project.expectedCost)}</span>
              </div>
            )}
          </div>
          {project.happeningFor && (
            <div className="flex items-center gap-1 text-sm">
              <Target className="h-3.5 w-3.5 text-primary" />
              <span className="text-muted-foreground">For: </span>
              <span className="line-clamp-1">{project.happeningFor}</span>
            </div>
          )}
          {project.hostClub && (
            <div className="flex items-center gap-1 text-sm">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Host club: </span>
              <span className="line-clamp-1">{project.hostClub}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}
